// Type-only: loads @tiptap/markdown's Editor augmentation (editor.markdown).
import { getHTMLFromFragment, type Editor, type JSONContent } from '@tiptap/core';
import type {} from '@tiptap/markdown';
import { Fragment } from '@tiptap/pm/model';
import type { EditorState } from '@tiptap/pm/state';
import { CellSelection } from '@tiptap/pm/tables';
import type { EditorView } from '@tiptap/pm/view';
import { serializeSectionPresentationMarkdown } from '../sections/section-markdown';
import { stripEmptySectionHeadingPresentationHtml } from '../sections/section-markers';

/**
 * Pure copy-payload construction (OpenSpec `monorepo-kit-editor` task 4.3;
 * the 5.5 `/core` move candidate). Serialization only — the clipboard writes,
 * word counts, and analytics stay in the app-side shell (`./index`).
 */
export type ClipboardPayload = { html: string; markdown: string };

/**
 * The fragment the current selection serializes as, for either clipboard flavor.
 *
 * A `CellSelection` is read through its own `content()` rather than `from`/`to`:
 * `prosemirror-tables` seeds the base `Selection` with the HEAD cell's range, so
 * those two positions span one cell, not the selected rect. `content()` returns
 * the whole table node only when the rect covers the table; any smaller rect
 * comes back as bare `tableRow`s, which render as a `<tr>` with no table around
 * it and as nothing at all in GFM, so those rows are re-wrapped in the source
 * table's node type to keep both flavors a real table.
 *
 * Shared with the vMarkdown copy path (`copySelectionToClipboard`), which owns
 * its own legacy HTML/markdown pipeline but needs the identical fragment.
 */
export const selectionClipboardFragment = (state: EditorState): Fragment => {
  const { selection } = state;
  if (!(selection instanceof CellSelection)) {
    return state.doc.slice(selection.from, selection.to).content;
  }

  const rows = selection.content().content;
  const table = selection.$anchorCell.node(-1);
  return rows.firstChild?.type === table.type
    ? rows
    : Fragment.from(table.type.create(table.attrs, rows));
};

/**
 * Serialize the current selection to a clipboard payload through the
 * `@tiptap/markdown` boundary. Presentation nodes serialize individually so
 * persistence-only doc delimiters are omitted; node overrides still emit
 * compact GFM. Never touches the legacy `simpleHtmlToMarkdown` pipeline.
 *
 * Returns `null` for a collapsed selection or unavailable markdown manager so
 * the caller leaves the browser default in place. A non-collapsed selection
 * that contains only presentation-hidden structure returns empty clipboard
 * flavors, preventing the browser from copying the raw editor sentinel.
 *
 * A `CellSelection` is read through {@link selectionClipboardFragment} rather
 * than `from`/`to`, which span only its head cell.
 */
export const buildClipboardPayload = (
  editor: Editor,
  view: EditorView
): ClipboardPayload | null => {
  const fragment = selectionClipboardFragment(view.state);
  if (fragment.size === 0) {
    return null;
  }

  const html = stripEmptySectionHeadingPresentationHtml(
    getHTMLFromFragment(fragment, view.state.schema)
  );
  // `Fragment.toJSON()` is `null` for an empty fragment; default to `[]`.
  const content = (fragment.toJSON() as JSONContent[] | null) ?? [];
  const markdownManager = editor.markdown;
  if (!markdownManager) {
    return null;
  }
  const markdown = serializeSectionPresentationMarkdown(content, (node) =>
    markdownManager.serialize(node)
  );

  return { html, markdown };
};
